import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  makeStyles
} from "@material-ui/core";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import useNavigator from "@saleor/hooks/useNavigator";
import { maybe } from "@saleor/misc";
import { shippingSection } from "@saleor/shipping/urls";
import {
  ActivityLogSortField,
  OrderDirection
} from "@saleor/types/globalTypes";
import moment from "moment";
import * as React from "react";

import RateEventItem from "../../components/RateEventItem";
import { useShippingZoneActivityLogs } from "../../queries";
import { ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node } from "../../types/ShippingZoneActivityLogs";
import { AuditLogsUrlQueryParams } from "../../urls";

const useStyles = makeStyles(
  _ => ({
    borderBottom: {
      borderBottom: "solid #CED3DD 0.5px"
    },
    loadMoreButton: {
      border: "1px solid #CED3DD40",
      borderRadius: "50px",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
      height: "40px",
      textTransform: "none",
      width: "185px"
    },
    pageHeader: {
      display: "flex",
      flexDirection: "row",
      marginTop: "10px"
    },
    textCenter: {
      textAlign: "center"
    }
  }),
  { name: "AuditLogsShippingRate" }
);

interface AuditLogsShippingRateProps {
  id: string;
  params: AuditLogsUrlQueryParams;
  pageName: string;
  type: "PRICE" | "LOCATION";
  cardTitle: string;
}

const AuditLogsShippingRate: React.FC<AuditLogsShippingRateProps> = props => {
  const { id, pageName, type, cardTitle } = props;
  const classes = useStyles({});
  const navigate = useNavigator();

  const breadcrumbDirectory = [
    {
      name: "Configuration",
      url: configurationOpsMenuUrl
    },
    {
      name: "Shipping Methods",
      url: shippingSection + id
    },
    {
      name: pageName
    }
  ];

  const sortBy = {
    sortBy: {
      direction: OrderDirection.DESC,
      field: ActivityLogSortField.CREATED_AT
    }
  };

  const { data, loading, loadMore } = useShippingZoneActivityLogs({
    displayLoader: true,
    variables: {
      ...sortBy,
      first: 100,
      subject: id,
      type: type.toLowerCase()
    }
  });

  const logs = maybe(() =>
    data.shippingZoneActivityLogs.edges.map(edges => edges.node)
  );

  const getDateOnly = (isoString: string) =>
    moment
      .utc(isoString)
      .local()
      .format("YYYY-MM-DD");

  const groupByDate = (
    events: ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node[]
  ) =>
    events &&
    events.length > 0 &&
    events.reduce((grouped: Record<string, any[]>, event) => {
      const date = getDateOnly(event.createdAt);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
      return grouped;
    }, {});

  const groupedEvents = logs && logs.length > 0 ? groupByDate(logs) : [];

  const onBack = () => {
    navigate(shippingSection + id);
  };

  const onFetchMore = () => {
    loadMore(
      (prev, next) => {
        if (
          prev.shippingZoneActivityLogs.pageInfo.endCursor ===
          next.shippingZoneActivityLogs.pageInfo.endCursor
        ) {
          return prev;
        }
        return {
          ...prev,
          shippingZoneActivityLogs: {
            ...prev.shippingZoneActivityLogs,
            edges: [
              ...prev.shippingZoneActivityLogs.edges,
              ...next.shippingZoneActivityLogs.edges
            ],
            pageInfo: next.shippingZoneActivityLogs.pageInfo
          }
        };
      },
      {
        after: data.shippingZoneActivityLogs.pageInfo.endCursor
      }
    );
  };

  const hasMore = maybe(
    () => data.shippingZoneActivityLogs.pageInfo.hasNextPage,
    false
  );

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div className={classes.pageHeader}>
        <PageBackNavigation onBack={() => onBack()} />
        <PageHeader title={pageName}></PageHeader>
      </div>

      <Card>
        <CardHeader
          className={classes.borderBottom}
          title={cardTitle}
        ></CardHeader>
        <CardContent>
          {logs && logs.length === 0 && !loading && (
            <div className={classes.textCenter}>No records found.</div>
          )}

          {logs && logs.length > 0 && (
            <>
              {Object.entries(groupedEvents).map(
                ([date, eventsByDate], index) => (
                  <React.Fragment
                    key={`auditLogsShippingRateEvent-${index}-${date}`}
                  >
                    {date && eventsByDate && (
                      <RateEventItem
                        type={type}
                        date={date}
                        eventsByDate={eventsByDate}
                      />
                    )}
                  </React.Fragment>
                )
              )}

              {!loading && hasMore && (
                <div className={classes.textCenter}>
                  <Button
                    className={classes.loadMoreButton}
                    color="primary"
                    onClick={onFetchMore}
                  >
                    Load More Activities
                  </Button>
                </div>
              )}
            </>
          )}

          {loading && (
            <div className={classes.textCenter}>
              <CircularProgress />
            </div>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AuditLogsShippingRate;
